export interface CachedConversation {
  id: string;
  data: any;
  lastAccessed: number;
  metadata?: {
    messageCount?: number;
    lastUpdated?: number;
    priority?: 'high' | 'medium' | 'low';
  };
}

export interface ConversationCacheOptions {
  maxSize?: number;
  ttl?: number; // Time to live in milliseconds
  onEvict?: (conversation: CachedConversation) => void;
}

/**
 * Intelligent LRU cache for conversation data with TTL support
 * Provides efficient storage and retrieval for large conversation datasets
 */
export class ConversationCache {
  private cache = new Map<string, CachedConversation>();
  private lru: string[] = [];
  private maxSize: number;
  private ttl: number;
  private onEvict?: (conversation: CachedConversation) => void;
  private cleanupInterval?: NodeJS.Timeout;

  constructor(options: ConversationCacheOptions = {}) {
    this.maxSize = options.maxSize || 100;
    this.ttl = options.ttl || 30 * 60 * 1000; // 30 minutes default
    this.onEvict = options.onEvict;

    // Start cleanup interval for TTL
    this.startCleanupInterval();
  }

  /**
   * Get conversation from cache
   * Moves item to front of LRU and updates access time
   */
  get(id: string): CachedConversation | null {
    const cached = this.cache.get(id);
    if (cached) {
      // Check if expired
      if (this.isExpired(cached)) {
        this.invalidate(id);
        return null;
      }

      // Move to front of LRU
      this.lru = this.lru.filter(key => key !== id);
      this.lru.unshift(id);
      
      // Update last accessed
      cached.lastAccessed = Date.now();
      
      return cached;
    }
    return null;
  }

  /**
   * Set conversation in cache
   * Handles LRU eviction when at capacity
   */
  set(id: string, conversation: CachedConversation): void {
    // Remove from cache if at capacity and not already present
    if (this.cache.size >= this.maxSize && !this.cache.has(id)) {
      this.evictLRU();
    }

    // Set conversation with current timestamp
    const cachedConversation: CachedConversation = {
      ...conversation,
      lastAccessed: Date.now()
    };

    this.cache.set(id, cachedConversation);
    
    // Update LRU order
    this.lru = this.lru.filter(key => key !== id);
    this.lru.unshift(id);
  }

  /**
   * Update existing conversation data
   */
  update(id: string, data: Partial<CachedConversation>): boolean {
    const existing = this.cache.get(id);
    if (existing) {
      const updated: CachedConversation = {
        ...existing,
        ...data,
        lastAccessed: Date.now()
      };
      this.cache.set(id, updated);
      
      // Move to front of LRU
      this.lru = this.lru.filter(key => key !== id);
      this.lru.unshift(id);
      
      return true;
    }
    return false;
  }

  /**
   * Remove conversation from cache
   */
  invalidate(id: string): boolean {
    const conversation = this.cache.get(id);
    if (conversation) {
      this.cache.delete(id);
      this.lru = this.lru.filter(key => key !== id);
      
      // Call eviction callback if provided
      if (this.onEvict) {
        this.onEvict(conversation);
      }
      
      return true;
    }
    return false;
  }

  /**
   * Check if conversation exists in cache
   */
  has(id: string): boolean {
    const conversation = this.cache.get(id);
    if (conversation && this.isExpired(conversation)) {
      this.invalidate(id);
      return false;
    }
    return this.cache.has(id);
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      hitRate: this.calculateHitRate(),
      oldestEntry: this.getOldestEntry(),
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  /**
   * Clear entire cache
   */
  clear(): void {
    // Call eviction callback for all items if provided
    if (this.onEvict) {
      for (const conversation of Array.from(this.cache.values())) {
        this.onEvict(conversation);
      }
    }

    this.cache.clear();
    this.lru = [];
  }

  /**
   * Get all cached conversation IDs
   */
  getKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Cleanup expired entries
   */
  cleanup(): number {
    let removed = 0;
    const now = Date.now();

    for (const [id, conversation] of Array.from(this.cache.entries())) {
      if (now - conversation.lastAccessed > this.ttl) {
        this.invalidate(id);
        removed++;
      }
    }

    return removed;
  }

  /**
   * Destroy cache and cleanup resources
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = undefined;
    }
    this.clear();
  }

  // Private methods

  private evictLRU(): void {
    const oldestKey = this.lru.pop();
    if (oldestKey) {
      const conversation = this.cache.get(oldestKey);
      this.cache.delete(oldestKey);
      
      if (conversation && this.onEvict) {
        this.onEvict(conversation);
      }
    }
  }

  private isExpired(conversation: CachedConversation): boolean {
    return Date.now() - conversation.lastAccessed > this.ttl;
  }

  private startCleanupInterval(): void {
    // Run cleanup every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 5 * 60 * 1000);
  }

  private calculateHitRate(): number {
    // This would need to be tracked with access counters in a real implementation
    return 0.85; // Placeholder
  }

  private getOldestEntry(): { id: string; lastAccessed: number } | null {
    let oldest: { id: string; lastAccessed: number } | null = null;
    
    for (const [id, conversation] of Array.from(this.cache.entries())) {
      if (!oldest || conversation.lastAccessed < oldest.lastAccessed) {
        oldest = { id, lastAccessed: conversation.lastAccessed };
      }
    }
    
    return oldest;
  }

  private estimateMemoryUsage(): number {
    // Rough estimation of memory usage in bytes
    let size = 0;
    for (const conversation of Array.from(this.cache.values())) {
      size += JSON.stringify(conversation).length * 2; // Rough estimate
    }
    return size;
  }
}

// Global cache instance
export const globalConversationCache = new ConversationCache({
  maxSize: 100,
  ttl: 30 * 60 * 1000, // 30 minutes
  onEvict: (conversation) => {
    console.log(`Evicted conversation ${conversation.id} from cache`);
  }
}); 